import {
  type ThemeColors,
  type SmartContract,
  type DefaultValues,
  type Signature,
  type CurrencyReference,
  type WidgetSettings,
  type WidgetCallbacks,
  WidgetIframeManager,
  IFRAME_STYLE,
  IFRAME_PERMISSIONS,
} from "@swipelux/onramp-core";

export class SwipeluxWidgetElement extends HTMLElement {
  private iframe: HTMLIFrameElement;
  private iframeManager?: WidgetIframeManager;
  private settings: Partial<WidgetSettings> = {};

  static get observedAttributes() {
    return [
      "widget-url",
      "api-key",
      "order-token",
      "payment-channel",
      "available-pay-currency",
      "available-receive-currency",
      "smart-contract-data",
      "default-values",
      "colors",
      "external-id",
      "signature",
      "force-new-transaction",
      "close-on-success",
    ];
  }

  constructor() {
    super();

    this.iframe = document.createElement("iframe");
    this.iframe.title = "Swipelux On-Ramp Widget";
    Object.assign(this.iframe.style, IFRAME_STYLE);
    this.iframe.allow = IFRAME_PERMISSIONS;
  }

  connectedCallback() {
    this.appendChild(this.iframe);
    this.initializeWidget();
  }

  disconnectedCallback() {
    this.iframeManager?.destroy();
  }

  // biome-ignore lint/suspicious/noExplicitAny: we have to use `any` here, sorry.
  private parseComplexAttribute(name: string, value: string): any {
    try {
      switch (name) {
        case "available-receive-currency": {
          try {
            return JSON.parse(value) as CurrencyReference;
          } catch {
            return value; // Return as string if not JSON
          }
        }
        case "smart-contract-data":
          return JSON.parse(value) as SmartContract;
        case "default-values":
          return JSON.parse(value) as DefaultValues;
        case "colors": {
          const colors = JSON.parse(value) as Partial<ThemeColors>;
          for (const color of Object.values(colors)) {
            if (!color.match(/^(#|rgb\(|rgba\()/)) {
              throw new Error("Invalid color format");
            }
          }
          return colors;
        }
        case "signature":
          return JSON.parse(value) as Signature;
        case "force-new-transaction":
        case "close-on-success":
          return value === "true";
        default:
          return value;
      }
    } catch (error) {
      console.error(`[Swipelux] Error parsing attribute ${name}:`, error);
      throw error;
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) {
      return;
    }

    const propName = name.replace(/-([a-z])/g, (_, letter) =>
      letter.toUpperCase()
    ) as keyof WidgetSettings;

    this.settings[propName] = this.parseComplexAttribute(name, newValue);
    this.initializeWidget();
  }

  private initializeWidget() {
    if (!this.isConnected || !this.settings.apiKey) {
      return;
    }

    this.iframeManager?.destroy();

    const callbacks: WidgetCallbacks = {
      onMessage: (event) => {
        this.dispatchEvent(new CustomEvent("message", { detail: event }));
      },
      onClose: () => {
        this.dispatchEvent(new CustomEvent("close"));
      },
    };

    this.iframeManager = new WidgetIframeManager(
      this.iframe,
      {
        apiKey: this.settings.apiKey,
        widgetUrl: this.settings.widgetUrl || "https://app.swipelux.com",
        ...this.settings,
      },
      callbacks
    );

    this.iframeManager.initialize();
  }

  public setColors(colors: Partial<ThemeColors>) {
    this.setAttribute("colors", JSON.stringify(colors));
  }

  public setDefaultValues(values: DefaultValues) {
    this.setAttribute("default-values", JSON.stringify(values));
  }

  public setSignature(signature: Signature) {
    this.setAttribute("signature", JSON.stringify(signature));
  }

  public setSmartContractData(data: SmartContract) {
    this.setAttribute("smart-contract-data", JSON.stringify(data));
  }

  public setExternalId(id: string | (() => Promise<string>)) {
    if (typeof id === "string") {
      this.setAttribute("external-id", id);
    } else {
      this.settings.externalId = id;
      this.initializeWidget();
    }
  }

  public setReceiveCurrency(currency: string | CurrencyReference) {
    if (typeof currency === "string") {
      this.setAttribute("available-receive-currency", currency);
    } else {
      this.setAttribute("available-receive-currency", JSON.stringify(currency));
    }
  }
}
